import React, { useState, useEffect, useMemo } from "react";
import { Text, Button, Flex } from "@jsq/ds/core";
import { SPACING } from "~/styles/variables";
import { SectionWithFields, TabWithSections } from "../../../CustomFields/types";
import { useBatchUpdateExtensionFieldValues } from "../../hooks/useUpdateExtensionFieldValue";
import {
  formatFieldValue,
  isValueChanged,
  sortExtensionFieldValues,
} from "../../utils/extensionFieldUtils";
import { FieldItem } from "./FieldItem";
import { SectionsView } from "./SectionsView";
import * as styles from "./styles_vanilla.css";
import { TabsView } from "./TabsView";
import type { ExtensionFieldValueObjectType } from "../../graphql";
import type { EntityExtensionFieldValue } from "../../types";

type ExtensionFieldValuesListProps = {
  extensionFieldValues: EntityExtensionFieldValue[];
  sectionsWithFields: SectionWithFields[];
  tabsWithSections: TabWithSections[];
  entityId: number;
  onDataRefresh?: () => void;
  onModalClose?: () => void;
};

export const ExtensionFieldValuesList: React.FC<ExtensionFieldValuesListProps> = ({
  extensionFieldValues,
  sectionsWithFields,
  tabsWithSections,
  entityId,
  onDataRefresh,
  onModalClose,
}) => {
  // Track form values and changes
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("");

  const { batchUpdate, loading: isSaving } = useBatchUpdateExtensionFieldValues(entityId);
  const sortedFields = useMemo(
    () => sortExtensionFieldValues(extensionFieldValues),
    [extensionFieldValues],
  );

  const fieldValuesMap = useMemo(() => {
    const map = new Map<string, EntityExtensionFieldValue>();
    for (const fieldValue of sortedFields) {
      if (fieldValue.field?.id) {
        map.set(fieldValue.field.id, fieldValue);
      }
    }
    return map;
  }, [sortedFields]);

  const viewMode = useMemo(() => {
    if (tabsWithSections.length > 0) return "tabs";
    if (sectionsWithFields.length > 0) return "sections";
    return "none";
  }, [tabsWithSections.length, sectionsWithFields.length]);

  useEffect(() => {
    if (!activeTab && viewMode === "tabs" && tabsWithSections.length > 0) {
      setActiveTab(tabsWithSections[0].tabName.toLowerCase().replace(/\s+/g, "-"));
    }
  }, [activeTab, viewMode, tabsWithSections]);

  useEffect(() => {
    const initialValues: Record<string, string> = {};

    extensionFieldValues.forEach((fieldValue) => {
      const fieldId = fieldValue.field?.id;
      if (!fieldId) return;

      const currentValue = formatFieldValue(
        fieldValue.value as ExtensionFieldValueObjectType,
        fieldValue.field.extensionFieldTypeId,
      );
      initialValues[fieldId] = currentValue;
    });

    setFormValues(initialValues);
  }, [extensionFieldValues]);

  const handleFieldChange = (fieldId: string, value: string) => {
    setFormValues((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!hasChanges || isSaving) return;

    const updates = Object.entries(formValues)
      .filter(([fieldId, value]) => {
        const originalField = extensionFieldValues.find((f) => f.field?.id === fieldId);
        if (!originalField?.field) return false;

        return isValueChanged(
          value,
          originalField.value as ExtensionFieldValueObjectType,
          originalField.field.extensionFieldTypeId,
        );
      })
      .map(([fieldId, value]) => ({
        extensionFieldId: fieldId,
        value,
      }));

    if (updates.length === 0) return;

    const result = await batchUpdate(updates);
    if (result && result.errors.length === 0) {
      setHasChanges(false);
      setFormValues({});

      if (onDataRefresh) {
        onDataRefresh();
      }

      if (onModalClose) {
        onModalClose();
      }
    }
  };

  const renderContent = () => {
    if (viewMode === "tabs") {
      return (
        <TabsView
          tabsWithSections={tabsWithSections}
          activeTab={activeTab}
          fieldValuesMap={fieldValuesMap}
          onTabChange={setActiveTab}
          onFieldChange={handleFieldChange}
        />
      );
    }

    if (viewMode === "sections") {
      return (
        <SectionsView
          sectionsWithFields={sectionsWithFields}
          fieldValuesMap={fieldValuesMap}
          onFieldChange={handleFieldChange}
        />
      );
    }

    return (
      <Flex flexDirection="column" gap={SPACING.MEDIUM}>
        {sortedFields.map((fieldValue) => (
          <FieldItem
            key={fieldValue.field?.id}
            fieldValue={fieldValue}
            onFieldChange={handleFieldChange}
          />
        ))}
      </Flex>
    );
  };

  if (sortedFields.length === 0) {
    return (
      <div className={styles.emptyFieldsList} data-testid="empty-fields-list">
        <Text variant="regular-md">No extension fields available</Text>
      </div>
    );
  }

  return (
    <div className={styles.fieldsFormContainer} data-testid="extension-fields-list">
      <div className={styles.fieldsHeader}>
        <Text variant="regular-lg" className={styles.fieldsTitle}>
          Extension Fields
        </Text>
        <Text variant="regular-sm" className={styles.fieldsSubtitle}>
          Edit field values and click Save to apply changes
        </Text>
      </div>

      {renderContent()}

      <div className={styles.saveButtonContainer}>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          data-testid="save-button"
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </div>
    </div>
  );
};

export default ExtensionFieldValuesList;
